<template>
  <div>
    <section class="mb-5">
      <SectionTitle>
        <span class="text-theme">3.</span>
        {{ $t('accountPage.stepName') }}
      </SectionTitle>

      <Loader class="mx-auto" v-if="loading" />
      <button
        v-if="!loading && !accounts.length"
        type="button"
        class="btn btn-theme"
        v-on:click="allDataRequest"
      >Try again</button>

      <div v-if="!loading">
        <AccountsItem
          v-for="(account, index) in accounts"
          :key="`account-${index}`"
          :accountData="account"
          :accountExtendedData="accountsExtended ? accountsExtended[account.resourceId] : {}"
        />
      </div>
    </section>

    <div v-if="accounts && accounts.length && !loading">
      <AccountsTransactions v-show="selectedAccounts.length" class="mb-5" />

      <section class="mb-3" v-show="accountsExtended && selectedAccounts.length">
        <SectionTitle>Export options</SectionTitle>
        <div class="row">
          <AccountsOptionsPeriod class="mb-3 col-12 col-lg-8 col-xl-7" />
          <AccountsOptionsFormat class="mb-3 col-12 col-lg-4 col-xl-5" />
        </div>
      </section>

      <section v-show="accountsExtended && selectedAccounts.length">
        <p>
          The cost of export is
          <b>{{currencySymbols[currency]}} {{price}}</b>
        </p>
        <div class="d-flex align-items-center flex-wrap">
          <PaymentCoupon
            class="mr-2 mb-2 paymet-coupon-outer"
            v-on:editing="isCouponEditing = $event"
            v-on:applying="isPaymentActionsDisabled = $event"
          />
          <button
            v-if="isCouponEditing"
            type="button"
            class="btn btn-outline-theme mr-2 mb-2 btn-sm-100"
            :disabled=isPaymentActionsDisabled
          >Apply coupon</button>
          <button
            v-if="!isCouponEditing"
            type="button"
            class="btn btn-theme mr-2 mb-2 btn-sm-100"
            :disabled=isPaymentActionsDisabled
            v-on:click.prevent="proceedToPayment"
          >Proceed to payment →</button>
          <img src="../assets/powered_by_stripe.svg" alt="stripe" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
import AccountsItem from '@/components/Accounts/AccountsItem'
import AccountsOptionsPeriod from '@/components/Accounts/AccountsOptions/AccountsOptionsPeriod'
import AccountsOptionsFormat from '@/components/Accounts/AccountsOptions/AccountsOptionsFormat'
import AccountsTransactions from '@/components/Accounts/AccountsTransactions/AccountsTransactions'
import PaymentCoupon from '@/components/Payment/PaymentCoupon'
import SectionTitle from '@/components/elements/SectionTitle'
import { SET_PERSONAL_STATE_VALUE } from '../store/modules/personal/variables'
import { REQUEST_INTERVAL, REQUESTS_AMOUNT_LIMIT, currencySymbols } from '../constants'

export default {
  name: 'AccountPersonal',
  data() {
    return {
      userErrors: [],
      accountsCheckInterval: null,
      currencySymbols,
      requestsAmount: 0,
      isCouponEditing: false,
      isPaymentActionsDisabled: false
    }
  },
  computed: {
    ...mapState('personal', ['selectedFileFormat', 'selectedDateRangeValue', 'loading']),
    ...mapGetters('personal', ['accounts', 'accountsExtended', 'selectedAccounts']),
    ...mapGetters('auth', ['expiresTime']),
    ...mapGetters('payment', ['price', 'currency'])
  },
  async mounted() {
    if (this.accounts && this.accounts.length && this.accountsExtended && Object.keys(this.accountsExtended).length) {
      return
    }
    await this.allDataRequest()
    if (this.selectedAccounts.length < 1) {
      for (const item of this.accounts) {
        this.setAccountSelected(item.resourceId)
      }
    }
    this.pushAlertMessage({ message: `The session expires in ${this.expiresTime} minutes`, type: 'info' })
  },
  beforeDestroy() {
    clearInterval(this.accountsCheckInterval)
  },
  components: {
    AccountsItem,
    AccountsOptionsPeriod,
    AccountsOptionsFormat,
    AccountsTransactions,
    SectionTitle,
    PaymentCoupon
  },
  methods: {
    ...mapMutations('personal', [SET_PERSONAL_STATE_VALUE]),
    ...mapActions('personal', ['accountsRequest', 'accountsExtendedRequest', 'setAccountSelected']),
    ...mapActions('base', ['pushAlertMessage']),
    ...mapActions('payment', ['exportPriceRequest']),
    proceedToPayment() {
      this.userErrors = []
      this.isPaymentActionsDisabled = true
      if (!this.selectedAccounts.length) {
        this.userErrors.push('account')
      }
      if (!this.selectedFileFormat) {
        this.userErrors.push('format')
      }
      if (!this.selectedDateRangeValue) {
        this.userErrors.push('dates')
      }
      for (const error of this.userErrors) {
        this.pushAlertMessage({ message: this.$t(`accountPage.errors.${error}`) })
      }
      if (!this.userErrors.length) {
        this.$router.push({ name: 'accountPayment' })
      }
    },
    getAccountsExtendedByInterval() {
      const getAccounts = async () => {
        const accounts = await this.accountsExtendedRequest()

        if (accounts) {
          clearInterval(this.accountsCheckInterval)
        }
        this.requestsAmount++
        if (this.requestsAmount > REQUESTS_AMOUNT_LIMIT) {
          clearInterval(this.sessionCheckInterval)
        }
      }
      getAccounts()
      this.accountsCheckInterval = setInterval(async () => getAccounts(), REQUEST_INTERVAL)
    },
    async allDataRequest() {
      await this.accountsRequest()
      this.getAccountsExtendedByInterval()
      this.exportPriceRequest()
    }
  }
}
</script>

<style lang="scss">
@import '../style/variables';
@import '~bootstrap/scss/mixins';

.format-select {
  min-width: 100px;
}
.paymet-coupon-outer {
  @include media-breakpoint-up(md) {
    width: 200px !important;
  }
}
</style>
